<template>
  <div class="w-full flex flex-col p-6">
    <!-- GENERAL SETTINGS & AVATAR UPLOAD SECTION -->
    <div class="flex mx-auto w-full mt-7" style="max-width: 900px">
      <div class="flex flex-col gap-1 mb-6 xl:mb-0 w-2/3" style="padding-right: 104px">
        <div v-if="isLensGuest" class="flex items-center p-0.5 rounded-md bg-neutral-25 cursor-default w-max mb-1.5">
          <GuestAccessIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="px-1 text-text-normal">
            Guest Access
          </BaseText>
        </div>
        <BaseText type="label" size="md" class="text-text-muted">
          General
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          Manage the superficial details of this Lens brand.
        </BaseText>
      </div>
      <div class="w-full">
        <!-- Brand Name -->
        <BaseInputV3 
          class="mb-2.5"
          label="Brand Name"
          :inputValue="lens.name"
          :isValid="!showErrors || form.companyName"
          :disabled="isLensGuest"
          placeholder="Your Brand"
          errorMessage="Brand name is required"
          required
          @update:inputValue="updateFormField('companyName', $event)"
        />
        <div class="flex gap-5 bg-background-normal rounded-md p-1.5 flex-1">
          <img v-if="avatarPreview" :src="avatarPreview" alt="Preview" class="w-11 h-11 rounded-md">
          <div v-else-if="!lens.avatar" class="w-11 h-11 flex items-center justify-center rounded-md bg-neutral-400">
            <span class="text-background-hover text-label-md font-medium">
              {{ getInitials(lens.name) }}
            </span>
          </div>
          <img v-else :src="lens.avatar" alt="Avatar" class="w-11 h-11 rounded-md">
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-2 py-1.5">
              <button class="standard-btn flex items-center gap-1.5 rounded-md pl-1.5 py-1.5 pr-2.5"
              :class="{'cursor-not-allowed': isLensGuest}"
              @click="handleFileInput" :disabled="isLensGuest">
                <input ref="fileInput" class="hidden" type="file" @change="handleFileUpload">
                <UploadIcon class="text-icon-normal" />
                <BaseText type="label" size="sm" class="text-text-muted">
                  Upload
                </BaseText>
              </button>
              <button class="px-2.5 py-1.5 rounded-md transition-colors"
              :class="hasAvatarToRemove && !isLensGuest ? 'hover:bg-neutral-25 text-text-muted' : 'cursor-default bg-neutral-10 text-text-disabled'"
              @click="removeAvatar" :disabled="isLensGuest">
                <BaseText type="label" size="sm">
                  Remove
                </BaseText>
              </button>
            </div>
            <BaseText type="body" size="sm" class="text-text-normal">
              We support PNGs and JPEGs.
            </BaseText>
          </div>
        </div>
      </div>
    </div>
    <!-- DATA SOURCES SECTION -->
    <div class="flex flex-col w-full mx-auto" style="max-width: 900px">
      <!-- divider -->
      <div class="w-full h-px my-7 bg-neutral-50" />
      <LensAdAccountConnector 
        :lens="lens"
      />
    </div>
    <!-- GUEST INVITE SECTION -->
    <div class="flex flex-col mx-auto w-full" style="max-width: 900px">
      <!-- divider -->
      <div class="w-full h-px my-7 bg-neutral-50" />
      <div class="w-full flex items-center justify-between">
        <div class="flex flex-col gap-1 mb-6 xl:mb-0 w-2/3" style="padding-right: 104px">
          <BaseText type="label" size="md" class="text-text-muted">
            Guest Users
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal">
            Invite clients or partners to collaborate on a connected lens account.
          </BaseText>
        </div>
        <transition>
          <BaseLoadingSpinnerCircle v-if="loadingGuestInvitations" :duration="1.2" class="text-text-disabled" />
        </transition>
      </div>
      <!-- Invite Button -->
      <button v-if="!isLensGuest" class="guest-invite-btn w-full flex items-center gap-2 mt-5 p-3"
      @click="showInviteGuestModal = true">
        <AddUserIcon class="text-icon-normal" />
        <BaseText type="label" size="sm" class="text-text-muted">
          Invite a guest
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          No charge, no stress.
        </BaseText>
      </button>
      <!-- Guest Invitations List -->
      <div ref="guestInvitationsList">
        <!-- <div v-if="loadingGuestInvitations" class="skeleton w-full h-11 rounded-md mt-2" /> -->
        <div v-if="guestInvitations.length > 0" class="mt-2">
          <div v-for="lensInvite in guestInvitations" :key="lensInvite.id" class="flex items-center px-3 py-1.5 gap-2">
            <div>
              <img src="../../assets/images/defaultProfileImage.webp" alt="Avatar" class="w-5 h-5 rounded-full">
            </div>
            <BaseText type="label" size="sm" class="text-text-muted w-52 truncate">
              {{ lensInvite?.email }}
            </BaseText>
            <div class="flex-1">
              <div class="flex items-center gap-1 px-1.5 py-0.5 rounded-md w-max" :class="`${lensInvite?.status}-tag`">
                <BaseText type="label" size="xs" class="capitalize">
                  {{ lensInvite?.status }}
                </BaseText>
              </div>
            </div>
            <!-- Leave Lens / Remove Guest Invitation Button -->
            <button class="invite-action-btn px-2.5 py-1.5 rounded-md hover:bg-background-hover flex items-center justify-center"
            :disabled="lensInvite?.canEdit" @click="handleRemoveGuestInvite(lensInvite)">
              <BaseLoadingSpinnerCircle v-if="loadingRemoveInvite === lensInvite?.id" class="text-icon-normal" />
              <BaseText v-else type="label" size="sm" class="text-text-muted">
                {{ lensInvite?.invitedUser?.id === getUser?.user_id ? 'Leave' : 'Remove' }}
              </BaseText>
            </button>
          </div>
        </div>
      </div>
      <!-- divider -->
      <div class="w-full h-px my-7 bg-neutral-50" />
    </div>
    <div class="flex flex-col mx-auto w-full" style="max-width: 900px">
      <!-- AD ACCOUNT SETTINGS SECTION -->
      <div class="flex flex-col mx-auto w-full" style="max-width: 900px">
        <!-- Header/Title -->
        <div class="flex flex-col gap-1">
          <BaseText type="label" size="md" class="text-text-muted">
            Reporting Settings
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal" style="max-width: 550px">
            Configure your primary goal metric and default attribution window.
          </BaseText>
        </div>
        <!-- Ad Account Settings -->
        <div class="flex flex-col gap-5 mt-5">
          <!-- Currency Label -->
          <div class="flex items-center px-1.5 py-1 rounded-md bg-neutral-25 w-max">
            <BaseText type="body" size="xs" class="text-text-muted">
              Currency
            </BaseText>
            <div class="mx-1.5 h-3 w-0 border-l border-border-normal" />
            <BaseText type="body" size="xs" class="text-text-muted">
              {{ currencyLabel }}
            </BaseText>
          </div>
          <!-- Goal Metrics - first row -->
          <div class="w-full grid grid-cols-2 lg:grid-cols-3 items-center gap-3">
            <!-- Meta Goal Metric -->
            <div v-if="activeGoalMetrics.meta" class="w-full flex flex-col gap-1">
              <div class="flex items-center gap-1">
                <MetaPlatformIcon :width="16" :height="16" />
                <BaseText type="label" size="sm" class="text-text-muted">
                  Meta Goal Metric
                </BaseText>
              </div>
              <GoalMetricSelector 
                platform="meta"
                :selected-metric="adAccountSettings.goalMetric.meta"
                :metrics-override="platformMetrics.meta"
                :loading-metrics="loadingMetrics"
                :disabled="isLensGuest"
                dropdown-only
                @changeMetric="handleSelectGoalMetric('meta', $event)"
              />
            </div>
            <!-- TikTok Goal Metric -->
            <div v-if="activeGoalMetrics.tiktok" class="w-full flex flex-col gap-1">
              <div class="flex items-center gap-1">
                <TikTokPlatformIcon :width="16" :height="16" />
                <BaseText type="label" size="sm" class="text-text-muted">
                  TikTok Goal Metric
                </BaseText>
              </div>
              <GoalMetricSelector 
                platform="tiktok"
                :selected-metric="adAccountSettings.goalMetric.tiktok"
                :metrics-override="platformMetrics.tiktok"
                :loading-metrics="loadingMetrics"
                :disabled="isLensGuest"
                dropdown-only
                @changeMetric="handleSelectGoalMetric('tiktok', $event)"
              />
            </div>
          </div>
          <!-- Attribution Window - second row -->
          <div>
            <BaseText type="label" size="sm" class="text-text-muted mb-1">
              Default Attribution Window
            </BaseText>
            <div class="grid grid-cols-2 lg:grid-cols-3 items-center gap-3 mt-1">
              <BaseSingleDropdown
                :disabled="isLensGuest"
                :options="clickAttributionWindows"
                class="z-10"
                option-key="label"
                :selected-obj="adAccountSettings.attributionWindowClickType || getAttributionWindowObj(lens.click_attribution_window, clickAttributionWindows)"
                placeholder="Click"
                use-parent-width
                @change="updateAdAccountField('attributionWindowClickType', $event)"
              />
              <BaseSingleDropdown
                :disabled="isLensGuest"
                :options="viewAttributionWindows"
                class="z-10"
                option-key="label"
                :selected-obj="adAccountSettings.attributionWindowViewType || getAttributionWindowObj(lens.view_attribution_window, viewAttributionWindows)"
                placeholder="View"
                use-parent-width
                @change="updateAdAccountField('attributionWindowViewType', $event)"
              />
            </div>
            <div class="w-full grid grid-cols-2 lg:grid-cols-3 items-center mt-3">
              <!-- Meta Only Disclaimer -->
              <div class="w-full col-span-2 flex items-center p-1 rounded-lg bg-neutral-25 whitespace-nowrap flex-nowrap">
                <div class="w-8 h-8 flex items-center justify-center">
                  <InfoIcon class="text-icon-normal" />
                </div>
                <BaseText type="label" size="sm" class="text-text-muted">
                  Whatever is set here will only affect Meta ads.
                </BaseText>
                <button class="ml-auto px-2.5 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-50"
                @click="showAttrWindowDisclaimer = !showAttrWindowDisclaimer">
                  <BaseText type="label" size="sm" class="text-text-muted">
                    Learn more
                  </BaseText>
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- divider -->
        <div class="w-full h-px my-7 bg-neutral-50" />
        <!-- Header/Title -->
        <div class="flex flex-col gap-1">
          <BaseText type="label" size="md" class="text-text-muted">
            Benchmarks
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal" style="max-width: 550px">
            Adjust your default segment for performance benchmarking to ensure you are comparing yourself to similar companies.
          </BaseText>
          <!-- Dropdowns -->
          <div class="rounded-xl grid grid-cols-2 lg:grid-cols-3 mt-4 gap-3 z-50">
            <!-- Industry Dropdown -->
            <BaseSingleDropdown
              show-label-space
              label="Industry / Niche"
              :options="industryOptions"
              class="flex-1"
              option-key="label"
              search-key="label"
              enable-search
              search-placeholder="Search Industries"
              :selected-obj="benchmarkSettings.selectedIndustry || getIndustryObj(lens.niche)"
              use-parent-width
              :max-height="176"
              :disabled="isLensGuest"
              @change="updateBenchmarkField('selectedIndustry', $event)"
            />
            <!-- AOV Dropdown -->
            <BaseSingleDropdown
              show-label-space
              label="Average Order Value (AOV)"
              :options="aovOptions"
              class="flex-1"
              option-key="label"
              :selected-obj="benchmarkSettings.selectedAOV || getAOVObj(lens.aov_min, lens.aov_max)"
              use-parent-width
              :max-height="176"
              :disabled="isLensGuest"
              @change="updateBenchmarkField('selectedAOV', $event)"
            />
            <!-- GMV/Revenue Dropdown -->
            <BaseSingleDropdown
              show-label-space
              label="GMV/Revenue"
              :options="revenueOptions"
              class="flex-1"
              option-key="label"
              :selected-obj="benchmarkSettings.selectedRevenue || getRevenueObj(lens.gmv)"
              use-parent-width
              :max-height="176"
              :disabled="isLensGuest"
              @change="updateBenchmarkField('selectedRevenue', $event)"
            />
          </div>
        </div>
      </div>
      <!-- divider -->
      <div class="w-full h-px my-7 bg-neutral-50" />
      <!-- DELETE LENS SECTION -->
      <div v-if="!isLensGuest" class="flex flex-col mx-auto w-full pb-6" style="max-width: 900px">
        <!-- Header/Title -->
        <div class="flex flex-col gap-1">
          <BaseText type="label" size="md" class="text-text-muted">
            Delete Lens Brand
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal">
            This will permanently delete all associated data sources and saved data.
          </BaseText>
        </div>
        <div class="mt-4">
          <BaseButton destroy @click="openConfirmDeleteLensModal">
            <BaseText type="label" size="sm" class="text-text-danger">
              Delete Lens
            </BaseText>
          </BaseButton>
        </div>
      </div>
    </div>
    <!-- Modals -->
    <ConfirmDeleteModal
      v-if="showConfirmDeleteLensModal"
      show-checkbox
      lens
      :loading="loadingDeleteLens"
      @confirm="handleDeleteLens"
      @close="showConfirmDeleteLensModal = false"
    />
    <InviteLensGuestModal
      v-if="showInviteGuestModal"
      :lens="lens"
      @invitationSent="handleInvitationSent"
      @close="showInviteGuestModal = false"
    />
    <AttributionWindowDisclaimerModal
      v-if="showAttrWindowDisclaimer"
      @close="showAttrWindowDisclaimer = false"
    />
  </div>
</template>

<script>
import { nanoid } from 'nanoid'
import { mixin as clickaway } from 'vue-clickaway2'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import _ from 'lodash'
import smoothReflow from 'vue-smooth-reflow'
import EventBus from '@/plugins/BaseNotifications/EventBus'
import firebase from '@/api/config/FirebaseConfig'
import FirebaseAPI from '@/api/firebase'
import LensAPI from '@/api/lens'

// Components
import LensAdAccountConnector from './lens/LensAdAccountConnector.vue'
import PropertySelectDropdown from '../lens/filters/PropertySelectDropdown.vue'

// Modals
import ConfirmDeleteModal from '../globals/ConfirmDeleteModal.vue'
import InviteLensGuestModal from '../lens/settings/InviteLensGuestModal.vue'
import AttributionWindowDisclaimerModal from './lens/AttributionWindowDisclaimerModal.vue'
import GoalMetricSelector from '../lens/creation/drawer/GoalMetricSelector.vue'

// Icons
import DefaultAvatarIcon from '../globals/Icons/DefaultAvatarIcon.vue'
import UploadIcon from '../globals/Icons/UploadIcon.vue'
import AddUserIcon from '../globals/Icons/AddUserIcon.vue'
import AnonBenchmarksIcon from '../globals/Icons/LensIcons/AnonBenchmarksIcon.vue'
import ChevronIcon from '../globals/Icons/ChevronIcon.vue'
import InfoIcon from '../globals/Icons/InfoSimpleIcon.vue'
import MetaPlatformIcon from '../globals/Icons/FilterIcons/Platforms/MetaPlatformIcon.vue'
import TikTokPlatformIcon from '../globals/Icons/FilterIcons/Platforms/TikTokPlatformIcon.vue'
import GuestAccessIcon from '../globals/Icons/LensIcons/GuestAccessIcon.vue'

// Static Data
import facebookAuthUrl from '@/api/config/FBAuthConfig'
import industryOptions from '../../utils/lens/industryNiches'

export default {
  name: 'LensSettings',
  components: {
    InviteLensGuestModal,
    PropertySelectDropdown,
    ConfirmDeleteModal,
    LensAdAccountConnector,
    GoalMetricSelector,
    AttributionWindowDisclaimerModal,
    DefaultAvatarIcon,
    UploadIcon,
    AddUserIcon,
    InfoIcon,
    AnonBenchmarksIcon,
    ChevronIcon,
    MetaPlatformIcon,
    TikTokPlatformIcon,
    GuestAccessIcon
  },
  mixins: [clickaway, smoothReflow],
  data () {
    return {      
      // Fetched Data      
      platformMetrics: {},
      guestInvitations: [],

      // Form Selections
      form: {
        companyName: ''
      },
      avatarPreview: '',
      avatarUpload: null,
      benchmarkSettings: {
        selectedIndustry: null,
        selectedAOV: null,
        selectedRevenue: null
      },
      adAccountSettings: {
        goalMetric: {
          meta: 'roas',
          tiktok: 'roas'
        },
        attributionWindowClickType: null,
        attributionWindowViewType: null
      },

      // Form Validation
      hasUnsavedChanges: false,
      submitAttempted: false,
      showErrors: false,

      // Form UI
      avatarWasRemoved: false,
      showConfirmDeleteLensModal: false,
      showInviteGuestModal: false,
      showAttrWindowDisclaimer: false,

      // Dropdown Options
      industryOptions,
      aovOptions: [
        { label: '<$100', value: '<$100' },
        { label: '$100+', value: '$100+' }
      ],
      revenueOptions: [
        { label: '<$1M', value: '<$1M' },
        { label: '$1M-$10M', value: '$1M-$10M' },
        { label: '$10M+', value: '$10M+' }
      ],
      clickAttributionWindows: [
        { label: 'None', value: null },
        { label: '1 Day Click', value: '1d' },
        { label: '7 Days Click', value: '7d' },
        { label: '28 Days Click', value: '28d' }
      ],
      viewAttributionWindows: [
        { label: 'None', value: null },
        { label: '1 Day View', value: '1d' },
        { label: '7 Days View', value: '7d' },
        { label: '28 Days View', value: '28d' }
      ],

      // Loading States
      loadingGuestInvitations: false,
      loadingRemoveInvite: false,
      loadingMetrics: false,
      loadingSubmit: false,
      loadingDeleteLens: false
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser', 'getGuestStatus', 'getTeam']),
    ...mapGetters('RouterModule', ['getCurrentRouteId']),
    ...mapGetters('LensModule', ['getLenses', 'getSidebarLenses']),
    hasAvatarToRemove () {
      return this.lens.avatar || this.avatarPreview
    },
    lens () {
      return this.getSidebarLenses.find(lens => lens.id === this.getCurrentRouteId)
    },
    activeGoalMetrics () {
      return {
        meta: this.lens?.accounts?.some(account => account.provider === 'facebook') ?? false,
        tiktok: this.lens?.accounts?.some(account => account.provider === 'tiktok') ?? false
      }
    },
    currencyLabel () {
      const currencyCode = this?.lens?.currency
      if (!currencyCode) return '--'

      // Get the currency name
      try {
        const dn = new Intl.DisplayNames(['en'], { type: "currency" });
        const name = dn.of(String(currencyCode).toUpperCase()) ?? currencyCode;
        return `${name} (${currencyCode.toUpperCase()})`
      } catch (error) {
        console.error('Error getting currency name:', error)
        return currencyCode.toUpperCase()
      }
    },
    isLensGuest () {
      return this.lens?.accessType === 'guest'
    }
  },
  watch: {
    // Add a watcher for the lens object
    lens: {
      handler (newLens, oldLens) {
        if (newLens && (!oldLens || newLens.id !== oldLens.id)) {
          // Re-initialize form data when lens changes
          this.initializeFromLensData()
        }
      },
      deep: true
    }
  },
  created () {
    // Register the save method with the parent component using the specific event name
    this.$root.$on('lens-settings-save-clicked', this.handleFormSubmit)
  },
  beforeDestroy () {
    // Clean up the event listener
    this.$root.$off('lens-settings-save-clicked', this.handleFormSubmit)
  },
  mounted () {
    // Initialize the form with lens data if available
    this.initializeFromLensData()
    this.$nextTick(() => {
      this.$smoothReflow({
        el: this.$refs.guestInvitationsList,
        property: 'height',
        transition: 'height 200ms ease-in-out'
      })
    })
  },
  beforeDestroy () {
    this.$unsmoothReflow({ el: this.$refs.guestInvitationsList })
  },
  methods: {
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    ...mapMutations('MiscModule', ['SET_SETTINGS_DRAWER_VIEW_MODE']),
    ...mapMutations('RouterModule', ['SET_CURRENT_ROUTE_ID']),
    async initializeFromLensData () {
      if (!this.lens) return
      this.loadingGuestInvitations = true
      this.loadingMetrics = true
      this.guestInvitations = []

      // Initialize form data from lens
      this.form = {
        companyName: this.lens.name || ''
      }

      // Initialize ad account settings from lens data
      this.adAccountSettings = {
        goalMetric: {
          meta: this.lens.goal_metric || 'roas',
          tiktok: this.lens.tiktok_goal_metric || 'roas'
        },
        attributionWindowClickType: this.getAttributionWindowObj(this.lens.click_attribution_window, this.clickAttributionWindows),
        attributionWindowViewType: this.getAttributionWindowObj(this.lens.view_attribution_window, this.viewAttributionWindows),
      }

      // Initialize benchmark settings from lens data
      this.benchmarkSettings = {
        selectedIndustry: this.getIndustryObj(this.lens.niche),
        selectedAOV: this.getAOVObj(this.lens.aov_min, this.lens.aov_max),
        selectedRevenue: this.getRevenueObj(this.lens.gmv)
      }

      if (!this._debouncedFetchLensData) {
        this._debouncedFetchLensData = _.debounce(this.fetchLensData, 300)
      }
      await this._debouncedFetchLensData()
    },
    async fetchLensData () {
      this.fetchLensInvitations()
      this.fetchLensMetrics()
    },
    async fetchLensInvitations () {
      try {
        const { data: guestInvitations } = await LensAPI.Lens.Invite.getAllByLensId(this.lens.id)
        this.guestInvitations = await Promise.all(guestInvitations.map(async invitation => {
          const invitedUser = { name: invitation.email }
          if (invitation.email?.toLowerCase() === this.getUser?.email?.toLowerCase()) {
            invitedUser.id = this.getUser?.user_id
          }
          const canEdit = await this.canRemoveInvite({ ...invitation, invitedUser })
          return {
            ...invitation,
            invitedUser,
            canEdit
          }
        }))
      } catch (error) {
        console.error('Error fetching lens invitations:', error)
      } finally {
        this.loadingGuestInvitations = false
      }
    },
    async fetchLensMetrics () {
      try {
        const metricsData = await LensAPI.LensMetrics.getLensMetrics(this.lens.id)
        this.platformMetrics = metricsData
      } catch (error) {
        console.error('Error fetching lens metrics:', error)
      } finally {
        this.loadingMetrics = false
      }
    },
    async handleRemoveGuestInvite (invite) {
      this.loadingRemoveInvite = invite?.id
      const copyOfGuestInvitations = [...this.guestInvitations]
      const invitationId = invite?.id
      try {
        await LensAPI.Lens.Invite.revoke(invitationId)
        this.guestInvitations = this.guestInvitations.filter(invite => invite.id !== invitationId)
        const isRemovingSelf = invite?.invitedUser?.id === this.getUser?.user_id
        // If we are removing our own invitation, go to lens overview
        if (isRemovingSelf) {
          this.SET_SETTINGS_DRAWER_VIEW_MODE('account')
          this.SET_CURRENT_ROUTE_ID(null)
          await this.fetchSidebarLenses()
        }
        this.$showAlert({
          message: isRemovingSelf ? 'Removed guest access successfully' : 'Lens invitation removed successfully',
          type: 'success'
        })
      } catch (error) {
        console.error(error)
        this.guestInvitations = copyOfGuestInvitations
        this.$showAlert({
          message: 'Failed to remove lens invitation. Please try again.',
          type: 'error'
        })
      } finally {
        this.loadingRemoveInvite = null
      }
    },
    canRemoveInvite(invite) {
      // Prevent removing invite if currently loading
      if (this.loadingRemoveInvite === invite?.id) return false

      const isTeamMember = this.getTeam?.id && (this.getTeam.id === invite?.team_id)
      const isGuest = this.getGuestStatus?.isGuestOnly || this.getGuestStatus?.isGuest
      const isInvitedUserSelf = invite?.invitedUser?.id === this.getUser?.user_id

      return isGuest && !isInvitedUserSelf && !isTeamMember
    },
    // Local state updates
    async handleInvitationSent (invitation) {
      const invitedUser = { name: invitation.email }
      if (invitation.email?.toLowerCase() === this.getUser?.email?.toLowerCase()) {
        invitedUser.id = this.getUser?.user_id
      }
      this.guestInvitations.push({
        ...invitation,
        invitedUser
      })
    },
    getAttributionWindowObj (value, options) {
      if (!value) return null
      return options.find(option => option.value === value) || null
    },
    getIndustryObj (nicheValue) {
      if (!nicheValue) return null
      return this.industryOptions.find(industry => industry.value === nicheValue) || null
    },
    getAOVObj (minValue, maxValue) {
      // Map the aov_min and aov_max to the appropriate AOV option
      if (minValue === null && maxValue === null) return null

      // This is a simple implementation - may need to be adjusted based on your actual data format
      if (minValue === 0 && maxValue === 100) {
        return this.aovOptions.find(option => option.value === '<$100') || null
      } else {
        return this.aovOptions.find(option => option.value === '$100+') || null
      }
    },
    getRevenueObj (gmvValue) {
      if (!gmvValue) return null

      // This is a simple implementation - may need to be adjusted based on your actual data format
      if (gmvValue < 1000000) {
        return this.revenueOptions.find(option => option.value === '<$1M') || null
      } else if (gmvValue < 10000000) {
        return this.revenueOptions.find(option => option.value === '$1M-$10M') || null
      } else {
        return this.revenueOptions.find(option => option.value === '$10M+') || null
      }
    },
    handleFileInput () {
      this.$refs.fileInput.click()
    },
    handleFileUpload (event) {
      const mediaFile = event.target.files[0]
      if (!mediaFile) return
      this.avatarUpload = mediaFile
      const fileType = mediaFile.type
      if (!(fileType === 'image/png' || fileType === 'image/jpeg')) {
        this.$showAlert({
          message: 'Only JPEG and PNG accepted',
          type: 'error'
        })
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        this.avatarPreview = e.target.result
        this.checkForChanges()
      }
      reader.readAsDataURL(mediaFile)
    },
    removeAvatar () {
      if (!this.hasAvatarToRemove) return
      this.avatarPreview = ''
      this.avatarUpload = null
      this.avatarWasRemoved = true
      this.checkForChanges()
    },
    updateBenchmarkField (field, value) {
      console.log(`updateBenchmarkField called with field: ${field}, value:`, value)

      // Create a new object with the updated field
      this.benchmarkSettings = {
        ...this.benchmarkSettings,
        [field]: value
      }

      console.log('Updated benchmarkSettings:', this.benchmarkSettings)
      this.checkForChanges()
    },
    async handleFormSubmit () {
      console.log('handleFormSubmit called in LensSettings')
      this.submitAttempted = true
      this.showErrors = true

      // Log the current state of benchmark and ad account settings
      console.log('Current benchmarkSettings:', JSON.stringify(this.benchmarkSettings))
      console.log('Current adAccountSettings:', JSON.stringify(this.adAccountSettings))

      // Check if the company name is filled in
      if (!this.form.companyName && !this.lens.name) {
        this.$showAlert({
          message: 'Please fill in all required fields',
          type: 'error'
        })
        return
      }

      this.loadingSubmit = true
      try {
        // Create an empty update payload and only add fields that have changed
        const lensUpdatePayload = {}

        // Only add name if it changed or if form.companyName is filled
        if (this.form.companyName && this.form.companyName !== this.lens.name) {
          lensUpdatePayload.name = this.form.companyName
          lensUpdatePayload.company_name = this.form.companyName
        }

        // Handle avatar changes
        if (this.avatarUpload) {
          // Upload new avatar
          const folderName = nanoid()
          const fileName = nanoid()
          const filePath = `${folderName}/${fileName}`
          const storageRef = firebase.app().storage('gs://foreplay-user-avatars').ref(filePath)

          await storageRef.put(this.avatarUpload)
          lensUpdatePayload.avatar = `https://storage.googleapis.com/foreplay-user-avatars/${filePath}`

          // Reset the avatarUpload after successful upload
          this.avatarUpload = null
          this.avatarPreview = ''
        } else if (this.avatarPreview === '' && this.lens.avatar && this.avatarWasRemoved) {
          // Only set to null if we explicitly removed the avatar
          lensUpdatePayload.avatar = null
        }

        // Add benchmark settings to the payload if they've changed
        if (this.benchmarkSettings.selectedIndustry &&
            this.benchmarkSettings.selectedIndustry.value !== this.lens.niche) {
          lensUpdatePayload.niche = this.benchmarkSettings.selectedIndustry.value
        }

        // Handle AOV settings
        if (this.benchmarkSettings.selectedAOV) {
          const newAovMin = this.getAOVMinValue(this.benchmarkSettings.selectedAOV)
          const newAovMax = this.getAOVMaxValue(this.benchmarkSettings.selectedAOV)

          // Only add to payload if values have changed
          if (newAovMin !== this.lens.aov_min) {
            lensUpdatePayload.aov_min = newAovMin
          }

          if (newAovMax !== this.lens.aov_max) {
            lensUpdatePayload.aov_max = newAovMax
          }
        }

        // Handle Revenue/GMV settings
        if (this.benchmarkSettings.selectedRevenue) {
          const newGmv = this.getRevenueValue(this.benchmarkSettings.selectedRevenue)
          if (newGmv !== this.lens.gmv) {
            lensUpdatePayload.gmv = newGmv
          }
        }

        // Add ad account settings to the payload if they've changed
        if (this.adAccountSettings.goalMetric.meta &&
            this.adAccountSettings.goalMetric.meta !== this.lens.goal_metric) {
          lensUpdatePayload.goal_metric = this.adAccountSettings.goalMetric.meta
        }
        if (this.adAccountSettings.goalMetric.tiktok &&
            this.adAccountSettings.goalMetric.tiktok !== this.lens.tiktok_goal_metric) {
          lensUpdatePayload.tiktok_goal_metric = this.adAccountSettings.goalMetric.tiktok
        }

        // Check that attribution window settings are not both null
        if (this.adAccountSettings.attributionWindowClickType &&
            this.adAccountSettings.attributionWindowClickType.value === null &&
            this.adAccountSettings.attributionWindowViewType &&
            this.adAccountSettings.attributionWindowViewType.value === null) {
          this.$showAlert({
            type: 'error',
            message: 'Must select at least one attribution window'
          })
          return
        }

        // Handle attribution window settings
        if (this.adAccountSettings.attributionWindowClickType &&
            this.adAccountSettings.attributionWindowClickType.value !== this.lens.click_attribution_window) {
          lensUpdatePayload.click_attribution_window = this.adAccountSettings.attributionWindowClickType.value
        }

        if (this.adAccountSettings.attributionWindowViewType &&
            this.adAccountSettings.attributionWindowViewType.value !== this.lens.view_attribution_window) {
          lensUpdatePayload.view_attribution_window = this.adAccountSettings.attributionWindowViewType.value
        }

        console.log('Updating lens with payload:', lensUpdatePayload)

        // Only update if there are changes
        if (Object.keys(lensUpdatePayload).length > 0) {
          // Update the lens using the API
          const response = await LensAPI.Lens.update(this.lens.id, lensUpdatePayload)
          console.log('Update response:', response)

          this.$showAlert({
            message: 'Lens settings updated successfully',
            type: 'success'
          })

          // Refresh lens data
          await this.fetchSidebarLenses()

          // Reset the changes flag
          this.hasUnsavedChanges = false
          this.$root.$emit('lens-settings-has-changes', false)
        } else {
          this.$showAlert({
            message: 'No changes to save',
            type: 'info'
          })
        }
      } catch (error) {
        console.error('Error updating lens settings:', error)
        const errMessage = error.response?.data?.message || 'Failed to update lens settings. Please try again.'
        this.$showAlert({
          message: errMessage,
          type: 'error'
        })
      } finally {
        this.loadingSubmit = false
        this.showErrors = false
      }
    },
    // Helper methods for converting dropdown selections to appropriate values
    getAOVMinValue (selectedAOV) {
      if (!selectedAOV) return this.lens.aov_min
      return selectedAOV.value === '<$100' ? 0 : 100
    },

    getAOVMaxValue (selectedAOV) {
      if (!selectedAOV) return this.lens.aov_max
      return selectedAOV.value === '<$100' ? 100 : null
    },

    getRevenueValue (selectedRevenue) {
      if (!selectedRevenue) return this.lens.gmv

      switch (selectedRevenue.value) {
        case '<$1M':
          return 999999
        case '$1M-$10M':
          return 5000000
        case '$10M+':
          return 10000000
        default:
          return null
      }
    },
    async deleteOldAvatar (filePath) {
      const storageRef = firebase.app().storage('gs://foreplay-user-avatars').ref()
      const oldAvatarRef = storageRef.child(filePath.split('https://storage.googleapis.com/foreplay-user-avatars/')[1])
      await oldAvatarRef.delete()
    },
    updateAdAccountField (field, value) {
      console.log(`updateAdAccountField called with field: ${field}, value:`, value)

      // Create a new object with the updated field
      this.adAccountSettings = {
        ...this.adAccountSettings,
        [field]: value
      }

      console.log('Updated adAccountSettings:', this.adAccountSettings)
      this.checkForChanges()
    },
    handleSelectGoalMetric (platform, key) {
      console.log('Goal metric selected:', key)
      // Update the goal metric in adAccountSettings
      this.adAccountSettings = {
        ...this.adAccountSettings,
        goalMetric: {
          ...this.adAccountSettings.goalMetric,
          [platform]: key
        }
      }
      console.log('Updated adAccountSettings:', this.adAccountSettings)
      this.checkForChanges()
    },
    updateFormField (field, value) {
      this.form = {
        ...this.form,
        [field]: value
      }
      this.checkForChanges()
    },
    async handleConnectMeta () {
      try {
        const popup = window.open(
          facebookAuthUrl,
          'facebook-auth',
          'width=600,height=600,scrollbars=yes,resizable=yes'
        )

        if (!popup) {
          throw new Error('Popup was blocked. Please allow popups for this site.')
        }
      } catch (error) {
        console.error('Facebook authentication error:', error)
      }
    },
    getInitials (name) {
      if (!name) return ''
      return name.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2)
    },
    checkForChanges () {
      // Check if any settings have changed from their original values
      let hasChanges = false

      // Check company name
      if (this.form.companyName && this.form.companyName !== this.lens.name) {
        hasChanges = true
      }

      // Check avatar changes
      if (this.avatarUpload || (this.avatarWasRemoved && this.lens.avatar)) {
        hasChanges = true
      }

      // Check benchmark settings
      if (this.benchmarkSettings.selectedIndustry &&
          this.benchmarkSettings.selectedIndustry.value !== this.lens.niche) {
        hasChanges = true
      }

      // Check AOV settings
      if (this.benchmarkSettings.selectedAOV) {
        const newAovMin = this.getAOVMinValue(this.benchmarkSettings.selectedAOV)
        const newAovMax = this.getAOVMaxValue(this.benchmarkSettings.selectedAOV)
        if (newAovMin !== this.lens.aov_min || newAovMax !== this.lens.aov_max) {
          hasChanges = true
        }
      }

      // Check Revenue/GMV settings
      if (this.benchmarkSettings.selectedRevenue) {
        const newGmv = this.getRevenueValue(this.benchmarkSettings.selectedRevenue)
        if (newGmv !== this.lens.gmv) {
          hasChanges = true
        }
      }

      // Check ad account settings
      if (this.adAccountSettings.goalMetric.meta &&
          this.lens.goal_metric &&
          this.adAccountSettings.goalMetric.meta !== this.lens.goal_metric) {
        hasChanges = true
      }
      if (this.adAccountSettings.goalMetric.tiktok &&
          this.lens.tiktok_goal_metric &&
          this.adAccountSettings.goalMetric.tiktok !== this.lens.tiktok_goal_metric) {
        hasChanges = true
      }

      // Check attribution window settings
      if (this.adAccountSettings.attributionWindowClickType &&
          this.adAccountSettings.attributionWindowClickType.value !== this.lens.click_attribution_window) {
        hasChanges = true
      }

      if (this.adAccountSettings.attributionWindowViewType &&
          this.adAccountSettings.attributionWindowViewType.value !== this.lens.view_attribution_window) {
        hasChanges = true
      }

      // Update the hasUnsavedChanges flag
      this.hasUnsavedChanges = hasChanges

      // Emit an event to notify parent components about the change status
      this.$root.$emit('lens-settings-has-changes', hasChanges)
    },
    openConfirmDeleteLensModal () {
      this.showConfirmDeleteLensModal = true
    },
    async handleDeleteLens () {
      try {
        this.loadingDeleteLens = true
        const deletedId = this.lens.id

        // Call the API to delete the lens
        await LensAPI.Lens.deleteLens(this.lens.id)

        // Show success message
        this.$showAlert({
          message: 'Lens deleted successfully',
          type: 'success'
        })

        await this.fetchSidebarLenses()

        // Take action depending on the user's current route context
        if (this.$route?.params?.lensId === deletedId) {
          this.$router.push({ name: 'LensView' }).catch(err => {
            console.error('Error navigating to lenses list:', err)
          })
        } else if (this.$route.name === 'LensView') {
          EventBus.$emit('lens:refresh-statuses')
        }
        this.$emit('close')
      } catch (error) {
        console.error('Error deleting lens:', error)
        const errMessage = error.response?.data?.message || 'Failed to delete lens. Please try again.'
        this.$showAlert({
          message: errMessage,
          type: 'error'
        })
      } finally {
        this.loadingDeleteLens = false
      }
    }
  }
}
</script>

<style scoped>
.section-grid {
  display: grid;
  grid-template-columns: 440px 440px;
}
@media (max-width: 1280px) {
  .section-grid {
    display: block;
    width: 100%;
    max-width: 640px;
  }
}

.standard-btn {
  background: linear-gradient(180deg, rgba(223, 225, 231, 0.08) 0%, rgba(223, 225, 231, 0.08) 100%), #FFFFFF;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out;
}
.standard-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}
.form-input {
  color: #06070F;
  border-radius: 6px;
  border: 1px solid;
  border-color: transparent;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: border-color 150ms ease-in-out, box-shadow 150ms ease-in-out;

  /* Body/Small */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.form-input:focus {
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.1), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}
.form-input::placeholder {
  color: #5E6678;
  transition: color 150ms ease-in-out;
  opacity: 0.9;
}
.form-input.invalid {
  box-shadow: none;
  border-color: #FF002F;
}
.form-input:hover::placeholder {
  color: #303546;
}
.form-input:focus::placeholder {
  color: #C1C7D0;
}
.reset-password-btn {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
}
.reset-password-btn.enabled:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}
.reset-password-btn.disabled {
  cursor: default;
  background-color: #F8FAFB;
  box-shadow: none;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0.25 }
  to { opacity: 1 }
}

@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-color {
  syntax: '<color>';
  initial-value: #0063F4;
  inherits: false;
}
.submit-btn {
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px #0063F4;
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled {
  color: #A4ABB8;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
}

.goal-metric-button {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out;
  border-radius: 8px;
}
.goal-metric-button:hover {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.12), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}
.goal-metric-dropdown {
  position: absolute;
  top: -4px;
  left: -2px;
  right: -2px;
  z-index: 500;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.guest-invite-btn {
  border-radius: 10px;
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 150ms ease-in-out;
}
.guest-invite-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.14), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.invite-status-badge{
  padding: 2px 4px;
  border-radius: 4px;
}

.accepted-tag{
  background-color: #EFFEFA;
  color: #184E44;
}

.pending-tag{
  background-color: #FFF6E0;
  color: #603A20;
}

.rejected-tag{
  background-color: #F2F4F7;
  color: #5E6678;
}

.invite-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
